'use client'

import { useCallback, useState } from 'react'

/**
 * "Remember Me" persistence — EMAIL ONLY.
 *
 * Security: the password is never read or written here. Only the email and the
 * checkbox state are persisted, mirroring the platform rule that the sole
 * sensitive value kept in localStorage is the `access_token`.
 */
export const REMEMBERED_EMAIL_KEY = 'rememberedEmail'
export const REMEMBER_ME_KEY = 'rememberMe'

function readEmail(): string {
  if (typeof window === 'undefined') {
    return ''
  }
  try {
    return window.localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''
  } catch {
    return ''
  }
}

function readRemember(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.localStorage.getItem(REMEMBER_ME_KEY) === 'true'
  } catch {
    return false
  }
}

export interface UseRememberedEmail {
  /** Email read once on mount, used to seed the form. SSR-safe. */
  initialEmail: string
  /** Checkbox state read once on mount. */
  initialRemember: boolean
  /** Persist (or clear) the remembered email after a successful login. */
  persist: (email: string, remember: boolean) => void
}

export function useRememberedEmail(): UseRememberedEmail {
  // Read localStorage once per mount so the value is stable across re-renders
  // and never runs during SSR.
  const [initial] = useState(() => ({
    email: readEmail(),
    remember: readRemember(),
  }))

  const persist = useCallback((email: string, remember: boolean) => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      if (remember && email) {
        window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email)
        window.localStorage.setItem(REMEMBER_ME_KEY, 'true')
      } else {
        window.localStorage.removeItem(REMEMBERED_EMAIL_KEY)
        window.localStorage.setItem(REMEMBER_ME_KEY, 'false')
      }
    } catch {
      // localStorage can be unavailable (private mode / quota); fail silently.
    }
  }, [])

  return {
    initialEmail: initial.email,
    initialRemember: initial.remember,
    persist,
  }
}

export default useRememberedEmail
